import { useEffect, useState } from 'react';
import { describeBatch, type SeparationState } from '@integrate/core';
import { canWrite, type ProjectRole, type ProposalSummary } from '@integrate/contracts';
import { api } from '../api.js';

/**
 * What the assistant is actually proposing, in the words of the commands themselves.
 *
 * THE GAP THIS CLOSES. The panel used to report a proposal as "Raised a proposal: <title> · 3
 * changes", where `title` is THE READER'S OWN SENTENCE truncated to 120 characters. So the one place
 * you could check whether the assistant had understood you echoed your words back at you and told you
 * a number. The real diff was two clicks away in Proposals, and rendered a planned hire as
 * "buildUp: 12 field(s)".
 *
 * TWO STATEMENTS, AND THE PAIR IS THE POINT. Above this, the thread shows the assistant's own
 * restatement — what it thinks you asked for. This shows what it will actually do. The restatement is
 * text a model wrote and can be wrong; this is derived from the commands and cannot be. So a
 * restatement reading "add them to the carve-out" sitting above a row reading "lands in: Remain — no
 * bucket set" is the misinterpretation made visible, and neither half alone would have shown it.
 *
 * AND IT IS WHERE THE PROPOSAL IS DECIDED, which is the second half of the same argument. The diff
 * was already here; sending somebody to another surface to press Apply meant leaving the conversation
 * that produced it, losing the restatement it has to be checked against, and finding the proposal
 * again in a list. The two questions — "is that what I meant" and "do it" — are one moment.
 *
 * WHAT IS NOT DUPLICATED IS THE RULE. `ProposalsModal` and this both draw buttons; the server decides.
 * An editor may not apply their own proposal — an editor can already write directly, so a
 * self-applied one is a way of making an unreviewed change look reviewed — and that rule reaches
 * assistant proposals too, because the person who ASKED is the one it was created by. Where it
 * applies, this says so rather than offering a button that would be refused.
 *
 * THE NEWEST TURN ONLY, fetched when it arrives. Every turn with a proposal COULD show this, and N
 * turns would be N requests for a transcript somebody is scrolling. The question this answers — "is
 * that what I meant?" — is about the thing that just happened; older turns keep their link to
 * Proposals, which is where a proposal is reviewed properly and applied.
 */
export function AssistantChanges({
  projectId,
  proposal,
  state,
  role,
  myUserId,
  onSettled,
}: {
  projectId: string;
  /** The proposal itself, not just its id: the status and the author decide what may be offered. */
  proposal: ProposalSummary;
  /**
   * The design, for resolving ids to names. Null while it loads — the rows still render, with ids
   * where names would be, which is worse to read and better than nothing to read.
   */
  state: SeparationState | null;
  role: ProjectRole | null;
  myUserId: string | null;
  /** The proposal after a decision, so the thread stops offering to decide it again. */
  onSettled(proposal: ProposalSummary): void;
}): JSX.Element | null {
  const proposalId = proposal.id;
  const [rows, setRows] = useState<{ label: string; fields: { key: string; value: string }[] }[] | null>(
    null,
  );
  const [error, setError] = useState('');
  /*
    A SECOND ERROR STATE, BECAUSE THE TWO FAILURES ARE NOT THE SAME KIND. A fetch that fails leaves
    nothing to draw, so it replaces the view. A refused decision leaves everything to draw — the diff
    is still right and the buttons are still the way to retry, and the commonest cause is that the
    proposal was decided in another tab, which the reader needs the row in front of them to make sense
    of. Sharing one state would have cleared the panel on a refusal and sent them looking.
  */
  const [decideError, setDecideError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setRows(null);
    setError('');
    void api
      .proposal(projectId, proposalId)
      .then((result) => {
        if (!live) return;
        const nameOf = (id: string): string | undefined =>
          state?.employees.find((e) => String(e.id) === id)?.name ??
          state?.design.buildUps.find((b) => String(b.id) === id)?.name;
        setRows(describeBatch(result.proposal.commands, nameOf));
      })
      .catch((cause: unknown) => {
        /*
          Reported rather than swallowed, and the proposal link above stays. This is the summary of a
          proposal that HAS been raised — a failure to render it must not read as a failure to raise it.
        */
        if (!live) return;
        setError(cause instanceof Error ? cause.message : 'Those changes could not be read back.');
      });
    return () => {
      live = false;
    };
  }, [projectId, proposalId, state]);

  if (error) {
    return (
      <p className="muted small" data-testid="assistant-changes-error">
        {error}
      </p>
    );
  }
  if (rows === null) return null;

  /*
    THE SAME TWO CONDITIONS THE SERVER CHECKS, drawn rather than guessed at. `canWrite` is the role
    gate; the second is the one that catches people out — an editor may not wave through a proposal
    they created, and an assistant proposal is created by whoever asked. The owner is exempt because
    there may be nobody else to ask.
  */
  const mine = myUserId !== null && proposal.createdBy === myUserId;
  const mayDecide = role !== null && canWrite(role) && (!mine || role === 'owner');

  const decide = (act: () => Promise<{ proposal: ProposalSummary }>): void => {
    if (busy) return;
    setBusy(true);
    setDecideError('');
    void act()
      .then((result) => onSettled(result.proposal))
      .catch((cause: unknown) =>
        setDecideError(cause instanceof Error ? cause.message : 'That could not be recorded.'),
      )
      .finally(() => setBusy(false));
  };

  return (
    <>
      <ul className="assistant-changes" data-testid="assistant-changes">
        {rows.map((row, i) => (
          <li key={i} data-testid={`assistant-change-${i}`}>
            <strong>{row.label}</strong>
            {row.fields.length > 0 && (
              <span className="muted"> — {row.fields.map((f) => `${f.key}: ${f.value}`).join(' · ')}</span>
            )}
          </li>
        ))}
      </ul>

      {proposal.status === 'open' && (
        <div className="sp-row assistant-decide">
          {mayDecide ? (
            <>
              <button
                className="primary"
                data-testid="assistant-approve"
                disabled={busy}
                onClick={() => decide(() => api.applyProposal(projectId, proposalId))}
              >
                {busy ? 'Applying…' : `Apply ${rows.length === 1 ? 'this change' : `these ${rows.length}`}`}
              </button>
              <button
                data-testid="assistant-reject"
                disabled={busy}
                onClick={() => decide(() => api.closeProposal(projectId, proposalId, 'rejected'))}
              >
                Reject
              </button>
            </>
          ) : mine ? (
            /*
              WITHDRAWING IS THE PROPOSER'S WORD FOR THE SAME ACT, and it is offered whether or not they
              may write — taking back your own suggestion is not an edit to the project.
            */
            <>
              <span className="muted small" data-testid="assistant-needs-reviewer">
                Somebody else has to apply this — that is what makes it reviewed.
              </span>
              <button
                data-testid="assistant-withdraw"
                disabled={busy}
                onClick={() => decide(() => api.closeProposal(projectId, proposalId, 'withdrawn'))}
              >
                Withdraw
              </button>
            </>
          ) : (
            <span className="muted small" data-testid="assistant-needs-editor">
              Waiting for somebody who can edit this project.
            </span>
          )}
        </div>
      )}

      {decideError !== '' && (
        <p className="muted small" data-testid="assistant-changes-error">
          {decideError}
        </p>
      )}

      {proposal.status !== 'open' && (
        <p className="muted small" data-testid="assistant-settled">
          {proposal.status === 'applied' ? 'Applied' : proposal.status === 'rejected' ? 'Rejected' : 'Withdrawn'}
          {proposal.reviewedByName ? ` by ${proposal.reviewedByName}` : ''}.
        </p>
      )}
    </>
  );
}
